import {
  createContext, useCallback, useContext, useEffect, useMemo, useRef, useState,
  type ChangeEvent, type FormEvent, type ReactNode,
} from 'react';
import { AccountingApiError } from '../api/http';

// ================================================================ Formato
/**
 * Evita que TypeScript infiera el genérico desde un callback (equivalente a NoInfer<T> de TS 5.4).
 * Integración Swiftmart.WEB (TypeScript 4.9): con esto el tipo sale de rows/value/options y no de onRowClick/onChange.
 */
type NoInfer<T> = [T][T extends any ? 0 : never];

// Formato de números según el país de la empresa: Costa Rica 1 234,50; Panamá y Honduras 1,234.50.
const LOCALES: Record<string, string> = { CR: 'es-CR', PA: 'es-PA', HN: 'es-HN' };
let numberLocale = 'es-CR';
let moneyFmt = new Intl.NumberFormat(numberLocale, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
/** Lo llama CompanyProvider con el país de la empresa (código CR, PA o HN). */
export function setNumberLocale(countryCode: string | null | undefined): void {
  const next = (countryCode && LOCALES[countryCode.toUpperCase()]) || 'es-CR';
  if (next === numberLocale) return;
  numberLocale = next;
  moneyFmt = new Intl.NumberFormat(numberLocale, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}
/** Número con el formato del país (porcentajes, tipos de cambio, años). */
export const fmtNumber = (v: number, maxDigits = 2) => v.toLocaleString(numberLocale, { maximumFractionDigits: maxDigits });
export const fmtMoney = (v: number | null | undefined) => (v === null || v === undefined || Number.isNaN(v) ? '' : moneyFmt.format(v));
export const fmtDate = (v: string | null | undefined) => {
  if (!v) return '';
  const s = v.slice(0, 10).split('-');
  return s.length === 3 ? `${s[2]}/${s[1]}/${s[0]}` : v;
};
export const fmtDateTime = (v: string | null | undefined) => (v ? `${fmtDate(v)} ${v.slice(11, 16)}` : '');
export const todayIso = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};
export const firstOfMonthIso = () => `${todayIso().slice(0, 8)}01`;
export const round2 = (v: number) => Math.round((v + Number.EPSILON) * 100) / 100;
export const errorMessage = (e: unknown) => (e instanceof AccountingApiError ? e.message : e instanceof Error ? e.message : 'Error inesperado.');

// ================================================================ Notificaciones y confirmación
interface Notice { id: number; kind: 'ok' | 'error' | 'info'; text: string }
interface UiContextValue {
  notify: (kind: Notice['kind'], text: string) => void;
  confirm: (text: string, opts?: { title?: string; danger?: boolean; requireReason?: boolean }) => Promise<{ ok: boolean; reason?: string }>;
}
const UiContext = createContext<UiContextValue | null>(null);

export function UiProvider({ children }: { children: ReactNode }) {
  return <RefreshProvider><UiCore>{children}</UiCore></RefreshProvider>;
}

function UiCore({ children }: { children: ReactNode }) {
  const [notices, setNotices] = useState<Notice[]>([]);
  const [dialog, setDialog] = useState<null | { text: string; title?: string; danger?: boolean; requireReason?: boolean; resolve: (r: { ok: boolean; reason?: string }) => void }>(null);
  const [reason, setReason] = useState('');
  const seq = useRef(0);

  const notify = useCallback((kind: Notice['kind'], text: string) => {
    const id = ++seq.current;
    setNotices((n) => [...n, { id, kind, text }]);
    setTimeout(() => setNotices((n) => n.filter((x) => x.id !== id)), kind === 'error' ? 9000 : 4500);
  }, []);
  const confirm = useCallback<UiContextValue['confirm']>((text, opts) => new Promise((resolve) => {
    setReason('');
    setDialog({ text, ...opts, resolve });
  }), []);
  const close = (ok: boolean) => {
    dialog?.resolve({ ok, reason: reason.trim() || undefined });
    setDialog(null);
  };
  const value = useMemo(() => ({ notify, confirm }), [notify, confirm]);

  return (
    <UiContext.Provider value={value}>
      {children}
      <div className="acc-notices" role="status" aria-live="polite">
        {notices.map((n) => <div key={n.id} className={`acc-notice acc-notice-${n.kind}`}>{n.text}</div>)}
      </div>
      {dialog && (
        <div className="acc-modal-backdrop" role="dialog" aria-modal="true" aria-label={dialog.title ?? 'Confirmar'}>
          <div className="acc-modal acc-modal-sm">
            <h3>{dialog.title ?? 'Confirmar'}</h3>
            <p>{dialog.text}</p>
            {dialog.requireReason && (
              <Field label="Motivo">
                <input className="acc-input" value={reason} onChange={(e) => setReason(e.target.value)} maxLength={200} autoFocus />
              </Field>
            )}
            <div className="acc-actions">
              <button type="button" className="acc-btn" onClick={() => close(false)}>Cancelar</button>
              <button type="button" className={`acc-btn ${dialog.danger ? 'acc-btn-danger' : 'acc-btn-primary'}`}
                disabled={dialog.requireReason && !reason.trim()} onClick={() => close(true)}>Aceptar</button>
            </div>
          </div>
        </div>
      )}
    </UiContext.Provider>
  );
}

export function useUi(): UiContextValue {
  const ctx = useContext(UiContext);
  if (!ctx) throw new Error('UiProvider no está montado.');
  return ctx;
}

// ================================================================ Actualizar datos (v4.3)
/**
 * "↻ Actualizar" de cada pantalla y actualización automática al volver a la ventana: todas las consultas (useAsync)
 * de la pantalla se repiten conservando filtros, pestaña y lo que ya se ve. No actúa con un formulario abierto.
 */
interface RefreshValue { key: number; busy: boolean; refresh: () => void; track: (p: Promise<unknown>) => void }
const RefreshContext = createContext<RefreshValue>({ key: 0, busy: false, refresh: () => undefined, track: () => undefined });
/** Segundos mínimos entre dos actualizaciones automáticas al volver a la ventana. */
const AUTO_REFRESH_MIN_SECONDS = 15;

export function RefreshProvider({ children }: { children: ReactNode }) {
  const [key, setKey] = useState(0);
  const [busy, setBusy] = useState(false);
  const pending = useRef(0);
  const waiting = useRef(false);
  const last = useRef(Date.now());
  const done = useCallback(() => { if (waiting.current && pending.current <= 0) { waiting.current = false; setBusy(false); } }, []);
  const refresh = useCallback(() => {
    if (waiting.current) return;
    last.current = Date.now();
    waiting.current = true;
    pending.current = 0;
    setBusy(true);
    setKey((k) => k + 1);
    setTimeout(done, 400);
  }, [done]);
  const track = useCallback((p: Promise<unknown>) => {
    if (!waiting.current) return;
    pending.current += 1;
    p.catch(() => undefined).finally(() => { pending.current -= 1; done(); });
  }, [done]);
  useEffect(() => {
    const onBack = () => {
      if (document.visibilityState !== 'visible') return;
      if (Date.now() - last.current < AUTO_REFRESH_MIN_SECONDS * 1000) return;
      // Con un formulario abierto no se actualiza: no se pierde lo que el usuario está escribiendo.
      if (document.querySelector('.acc-modal-backdrop')) return;
      refresh();
    };
    document.addEventListener('visibilitychange', onBack);
    window.addEventListener('focus', onBack);
    return () => { document.removeEventListener('visibilitychange', onBack); window.removeEventListener('focus', onBack); };
  }, [refresh]);
  const value = useMemo(() => ({ key, busy, refresh, track }), [key, busy, refresh, track]);
  return <RefreshContext.Provider value={value}>{children}</RefreshContext.Provider>;
}

export function RefreshButton() {
  const { busy, refresh } = useContext(RefreshContext);
  return <Button busy={busy} onClick={refresh} title="Volver a consultar los datos de esta pantalla">↻ Actualizar</Button>;
}

// ================================================================ Datos asíncronos
/**
 * refreshable = false: la consulta no se repite con "Actualizar" (formularios que copian los datos a su estado
 * para editarlos; repetirla borraría lo que el usuario cambió).
 */
export function useAsync<T>(fn: () => Promise<T>, deps: unknown[], enabled = true, refreshable = true) {
  const { key, track } = useContext(RefreshContext);
  const refreshKey = refreshable ? key : 0;
  const [data, setData] = useState<T | undefined>(undefined);
  const [error, setError] = useState<unknown>(null);
  const [loading, setLoading] = useState(enabled);
  const [tick, setTick] = useState(0);
  useEffect(() => {
    if (!enabled) { setLoading(false); return; }
    let alive = true;
    setLoading(true);
    setError(null);
    const p = fn();
    track(p);
    p.then((d) => { if (alive) setData(d); }).catch((e) => { if (alive) setError(e); }).finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [...deps, tick, enabled, refreshKey]);
  const reload = useCallback(() => setTick((t) => t + 1), []);
  return { data, error, loading, reload, setData };
}

/**
 * Ejecuta una operación con protección contra doble envío (instrucción 63): mientras corre, busy = true y una
 * segunda llamada se ignora. Muestra el mensaje de éxito o el error uniforme de la API.
 */
export function useAction() {
  const { notify, confirm } = useUi();
  const [busy, setBusy] = useState(false);
  const running = useRef(false);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string[]>>({});
  const run = useCallback(async <R,>(work: (reason?: string) => Promise<R>, opts?: { success?: string; confirm?: string; danger?: boolean; requireReason?: boolean }): Promise<R | undefined> => {
    if (running.current) return undefined;
    let reason: string | undefined;
    if (opts?.confirm) {
      const r = await confirm(opts.confirm, { danger: opts.danger, requireReason: opts.requireReason });
      if (!r.ok) return undefined;
      reason = r.reason;
    }
    running.current = true;
    setBusy(true);
    setFieldErrors({});
    try {
      const result = await work(reason);
      if (opts?.success) notify('ok', opts.success);
      return result;
    } catch (e) {
      if (e instanceof AccountingApiError) setFieldErrors(e.fieldErrors);
      notify('error', errorMessage(e));
      return undefined;
    } finally {
      running.current = false;
      setBusy(false);
    }
  }, [confirm, notify]);
  return { run, busy, fieldErrors };
}

// ================================================================ Controles
export function Button({ children, kind = 'default', busy, type = 'button', ...rest }: { children: ReactNode; kind?: 'default' | 'primary' | 'danger' | 'link'; busy?: boolean; type?: 'button' | 'submit'; onClick?: () => void; disabled?: boolean; title?: string }) {
  return (
    <button type={type} className={`acc-btn${kind !== 'default' ? ` acc-btn-${kind}` : ''}`} disabled={busy || rest.disabled} onClick={rest.onClick} title={rest.title} aria-busy={busy || undefined}>
      {busy ? <span className="acc-spin" aria-hidden="true" /> : null}{children}
    </button>
  );
}

export function Field({ label, children, error, hint, wide }: { label: string; children: ReactNode; error?: string[] | string; hint?: string; wide?: boolean }) {
  const errs = Array.isArray(error) ? error : error ? [error] : [];
  return (
    <label className={`acc-field${wide ? ' acc-field-wide' : ''}${errs.length ? ' acc-field-error' : ''}`}>
      <span className="acc-label">{label}</span>
      {children}
      {hint && !errs.length ? <small className="acc-hint">{hint}</small> : null}
      {errs.map((e) => <small key={e} className="acc-error-text">{e}</small>)}
    </label>
  );
}

type InputProps = { value: string | number | null | undefined; onChange: (v: string) => void; type?: string; placeholder?: string; required?: boolean; maxLength?: number; disabled?: boolean; min?: string | number; max?: string | number; step?: string; list?: string };
export function Input({ value, onChange, type = 'text', ...rest }: InputProps) {
  return <input className="acc-input" type={type} value={value ?? ''} onChange={(e: ChangeEvent<HTMLInputElement>) => onChange(e.target.value)} {...rest} />;
}
export function NumberInput({ value, onChange, step = '0.01', ...rest }: { value: number | null | undefined; onChange: (v: number) => void; step?: string; min?: number; max?: number; disabled?: boolean; required?: boolean }) {
  return <input className="acc-input acc-num" type="number" step={step} value={value === null || value === undefined || Number.isNaN(value) ? '' : value}
    onChange={(e) => onChange(e.target.value === '' ? 0 : Number(e.target.value))} {...rest} />;
}
export function Select<V extends string | number>({ value, onChange, options, placeholder, disabled, required }: { value: V | '' | null | undefined; onChange: (v: NoInfer<V>) => void; options: { value: V; label: string }[]; placeholder?: string; disabled?: boolean; required?: boolean }) {
  const isNum = options.length > 0 && typeof options[0].value === 'number';
  return (
    <select className="acc-input" value={value === null || value === undefined ? '' : String(value)} disabled={disabled} required={required}
      onChange={(e) => onChange((isNum ? (e.target.value === '' ? '' : Number(e.target.value)) : e.target.value) as V)}>
      {placeholder !== undefined && <option value="">{placeholder}</option>}
      {options.map((o) => <option key={String(o.value)} value={String(o.value)}>{o.label}</option>)}
    </select>
  );
}
export function Check({ checked, onChange, label }: { checked: boolean; onChange: (v: boolean) => void; label: string }) {
  return <label className="acc-check"><input type="checkbox" checked={checked} onChange={(e) => onChange(e.target.checked)} /> {label}</label>;
}

export function Form({ onSubmit, children, className }: { onSubmit: () => void; children: ReactNode; className?: string }) {
  return <form className={className ?? 'acc-form'} onSubmit={(e: FormEvent) => { e.preventDefault(); onSubmit(); }} noValidate={false}>{children}</form>;
}

export function Spinner({ text = 'Cargando…' }: { text?: string }) {
  return <div className="acc-loading"><span className="acc-spin" aria-hidden="true" /> {text}</div>;
}
export function ErrorBox({ error, onRetry }: { error: unknown; onRetry?: () => void }) {
  if (!error) return null;
  return <div className="acc-alert acc-alert-error">{errorMessage(error)} {onRetry && <Button kind="link" onClick={onRetry}>Reintentar</Button>}</div>;
}
export function Alert({ kind = 'info', children }: { kind?: 'info' | 'warn' | 'error' | 'ok'; children: ReactNode }) {
  return <div className={`acc-alert acc-alert-${kind}`}>{children}</div>;
}
export function Loadable<T>({ state, children }: { state: { data?: T; loading: boolean; error: unknown; reload: () => void }; children: (data: T) => ReactNode }) {
  if (state.loading && state.data === undefined) return <Spinner />;
  if (state.error) return <ErrorBox error={state.error} onRetry={state.reload} />;
  if (state.data === undefined) return null;
  return <>{children(state.data)}</>;
}

export interface Column<T> { key: string; title: string; render: (row: T) => ReactNode; align?: 'right' | 'center'; width?: string }
export function Table<T>({ rows, columns, rowKey, empty = 'Sin registros.', onRowClick, footer, selected }: { rows: T[]; columns: Column<T>[]; rowKey: (r: T) => string | number; empty?: string; onRowClick?: (r: NoInfer<T>) => void; footer?: ReactNode; selected?: string | number | null }) {
  return (
    <div className="acc-table-wrap">
      <table className="acc-table">
        <thead><tr>{columns.map((c) => <th key={c.key} style={{ textAlign: c.align, width: c.width }}>{c.title}</th>)}</tr></thead>
        <tbody>
          {rows.length === 0 && <tr><td colSpan={columns.length} className="acc-empty">{empty}</td></tr>}
          {rows.map((r) => {
            const k = rowKey(r);
            return (
              <tr key={k} onClick={onRowClick ? () => onRowClick(r) : undefined} className={`${onRowClick ? 'acc-clickable' : ''}${selected === k ? ' acc-selected' : ''}`}>
                {columns.map((c) => <td key={c.key} style={{ textAlign: c.align }}>{c.render(r)}</td>)}
              </tr>
            );
          })}
        </tbody>
        {footer ? <tfoot>{footer}</tfoot> : null}
      </table>
    </div>
  );
}
export const money = <T,>(key: string, title: string, get: (r: T) => number | null | undefined): Column<T> => ({ key, title, align: 'right', render: (r) => fmtMoney(get(r)) });

export function Pager({ page, totalPages, total, onPage }: { page: number; totalPages: number; total: number; onPage: (p: number) => void }) {
  if (totalPages <= 1) return <div className="acc-pager"><span>{total} registro(s)</span></div>;
  return (
    <div className="acc-pager">
      <Button disabled={page <= 1} onClick={() => onPage(page - 1)}>‹ Anterior</Button>
      <span>Página {page} de {totalPages} · {total} registro(s)</span>
      <Button disabled={page >= totalPages} onClick={() => onPage(page + 1)}>Siguiente ›</Button>
    </div>
  );
}

export function Modal({ title, onClose, children, size = 'md' }: { title: string; onClose: () => void; children: ReactNode; size?: 'sm' | 'md' | 'lg' }) {
  useEffect(() => {
    const h = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', h);
    return () => window.removeEventListener('keydown', h);
  }, [onClose]);
  return (
    <div className="acc-modal-backdrop" role="dialog" aria-modal="true" aria-label={title}>
      <div className={`acc-modal acc-modal-${size}`}>
        <div className="acc-modal-head"><h3>{title}</h3><button type="button" className="acc-x" aria-label="Cerrar" onClick={onClose}>×</button></div>
        {children}
      </div>
    </div>
  );
}

export function Tabs<K extends string>({ tabs, active, onChange }: { tabs: { key: K; label: string }[]; active: K; onChange: (k: NoInfer<K>) => void }) {
  return (
    <div className="acc-tabs" role="tablist">
      {tabs.map((t) => <button key={t.key} type="button" role="tab" aria-selected={active === t.key} className={`acc-tab${active === t.key ? ' acc-tab-active' : ''}`} onClick={() => onChange(t.key)}>{t.label}</button>)}
    </div>
  );
}

export function PageHeader({ title, subtitle, actions }: { title: string; subtitle?: string; actions?: ReactNode }) {
  return (
    <div className="acc-page-head">
      <div><h2>{title}</h2>{subtitle && <p className="acc-muted">{subtitle}</p>}</div>
      <div className="acc-actions">{actions}<RefreshButton /></div>
    </div>
  );
}
export function Toolbar({ children }: { children: ReactNode }) { return <div className="acc-toolbar">{children}</div>; }
export function Card({ title, children, actions }: { title?: string; children: ReactNode; actions?: ReactNode }) {
  return <section className="acc-card">{(title || actions) && <div className="acc-card-head">{title && <h3>{title}</h3>}{actions}</div>}{children}</section>;
}
export function Stat({ label, value, tone }: { label: string; value: ReactNode; tone?: 'pos' | 'neg' }) {
  return <div className={`acc-stat${tone ? ` acc-stat-${tone}` : ''}`}><span>{label}</span><strong>{value}</strong></div>;
}
export function Badge({ children, tone = 'neutral' }: { children: ReactNode; tone?: 'neutral' | 'ok' | 'warn' | 'bad' }) {
  return <span className={`acc-badge acc-badge-${tone}`}>{children}</span>;
}
export function entryTone(status: string): 'neutral' | 'ok' | 'warn' | 'bad' {
  return status === 'Apli' ? 'ok' : status === 'Anu' ? 'bad' : status === 'A' ? 'warn' : 'neutral';
}

/** Estado de formulario simple: set('campo', valor). */
export function useForm<T extends object>(initial: T) {
  const [values, setValues] = useState<T>(initial);
  const set = useCallback(<K extends keyof T>(key: K, value: T[K]) => setValues((v) => ({ ...v, [key]: value })), []);
  return { values, set, setValues, reset: () => setValues(initial) };
}
